<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header -->
      <div class="modal-header modal-header-counter">
        <div class="header-left">
          <h2 class="modal-title">{{ $ml.get('counter_details') }}</h2>
          <p class="event-subtitle">
            {{ sessionTitle }}
          </p>
        </div>

        <button class="close-button" @click="closeModal" :title="$ml.get('close')">
          <span class="mdi mdi-close"></span>
        </button>
      </div>

      <!-- Summary -->
      <div class="event-description event-description-counter">
        <div class="description-item">
          <label>{{ $ml.get('start_time') }}:</label>
          <span>{{ formatDateTimeFromTs(session?.timestamp_start) }}</span>
          <div class="timestamp-raw">{{ formatRawTimestamp(session?.timestamp_start) }}</div>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('end_time') }}:</label>
          <span>{{ formatDateTimeFromTs(session?.timestamp_end) }}</span>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('duration') || 'Duración' }}:</label>
          <span>{{ formatDurationMs(durationMs) }}</span>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('events_count') }}:</label>
          <span>{{ (events || []).length }}</span>
        </div>
      </div>

      <!-- Content -->
      <div class="modal-content modal-content-counter">
        <div class="content-grid">
          <!-- Video -->
          <div class="video-section">
            <h3>{{ $ml.get('video_event') }}</h3>
            <div class="video-container">
              <div v-if="effectiveVideoUrl" ref="videoStage" class="video-stage">
                <video
                  ref="eventVideo"
                  :src="effectiveVideoUrl"
                  controls
                  class="event-video"
                  @error="onVideoError"
                  @loadeddata="onVideoLoaded"
                  @timeupdate="onVideoTimeUpdate"
                >
                  {{ $ml.get('browser_not_support_video') }}
                </video>
                <canvas
                  ref="bboxCanvas"
                  class="bbox-canvas"
                ></canvas>
              </div>
              <div v-if="effectiveVideoUrl" class="video-meta">
                <div class="video-meta-row">
                  <span class="video-meta-label">Video ts:</span>
                  <span class="video-meta-value">{{ formatTimestampMs(getClipStartMs()) }}</span>
                </div>
                <div class="video-meta-row">
                  <span class="video-meta-label">t (s):</span>
                  <span class="video-meta-value">{{ currentVideoSeconds.toFixed(3) }}</span>
                  <span class="video-meta-sep">|</span>
                  <span class="video-meta-label">ui:</span>
                  <span class="video-meta-value">{{ formatMmSs(Math.round(currentVideoSeconds)) }}</span>
                  <span class="video-meta-sep">|</span>
                  <span class="video-meta-label">abs ts:</span>
                  <span class="video-meta-value">{{ formatTimestampMs(currentVideoAbsTimestampMs) }}</span>
                </div>
              </div>
              <div v-else class="no-video">
                <span class="mdi mdi-video-off"></span>
                <p>{{ $ml.get('no_video_available') || 'No hay video disponible' }}</p>
              </div>
            </div>
          </div>

          <!-- Events table -->
          <div class="events-table-section">
            <h3>{{ $ml.get('event_information') }}</h3>
            <div class="table-container">
              <div class="table-wrapper">
                <table class="events-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{{ $ml.get('seg') }}</th>
                      <th>{{ $ml.get('date') }}</th>
                      <th>{{ $ml.get('tag') }}</th>
                    </tr>
                  </thead>
                </table>
                <div class="table-body-scroll">
                  <table class="events-table">
                    <tbody>
                      <tr v-if="loadingEvents">
                        <td colspan="4" class="loading-events">
                          <i class="fas fa-spinner fa-spin"></i>
                          {{ $ml.get('loading_events') || 'Cargando eventos...' }}
                        </td>
                      </tr>
                      <template v-else-if="getPaginatedEvents().length === 0">
                        <tr>
                          <td colspan="5" class="no-events">
                            {{ $ml.get('no_events') }}
                          </td>
                        </tr>
                      </template>
                      <template v-else>
                        <tr
                          v-for="(event, index) in getPaginatedEvents()"
                          :key="event.id || event._id || `counter-event-${index}`"
                          class="event-row"
                          @mouseenter="onRowHover(event)"
                          @mouseleave="onRowLeave"
                        >
                          <td>{{ getEventNumber(index) }}</td>
                          <td>{{ formatEventVideoSeconds(event) }}</td>
                          <td>{{ formatEventDate(event) }}</td>
                          <td>{{ event.tag || '-' }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="!loadingEvents && (events || []).length > 0" class="table-pagination">
                <div class="pagination-info">
                  Mostrando {{ getPaginationStart() }} - {{ getPaginationEnd() }} de {{ (events || []).length }} eventos
                </div>
                <div class="pagination-controls">
                  <button
                    class="btn-pagination"
                    @click="previousPage"
                    :disabled="currentPage === 1"
                    :title="$ml.get('previous') || 'Anterior'"
                  >
                    <span class="mdi mdi-chevron-left"></span>
                  </button>
                  <span class="page-info">
                    {{ getPageOfText() }}
                  </span>
                  <button
                    class="btn-pagination"
                    @click="nextPage"
                    :disabled="currentPage >= totalPages"
                    :title="$ml.get('next')"
                  >
                    <span class="mdi mdi-chevron-right"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CounterEventDetailsModal',
  props: {
    isVisible: { type: Boolean, default: false },
    session: { type: Object, default: null },
    events: { type: Array, default: () => [] },
    loadingEvents: { type: Boolean, default: false },
    videoUrl: { type: String, default: null },
    videoContextTimestamp: { type: [Number, String], default: null }
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      videoError: false,
      localVideoUrl: null,
      clipContextTimestampMs: null,
      bboxCanvasContext: null,
      currentHoverEvent: null,
      currentHoverEventVideoTime: null,
      currentVideoSeconds: 0,
      currentVideoAbsTimestampMs: null,
      pendingHoverEvent: null,
      lastVideoSearchTimestamp: null,
      hoverVideoLoading: false
    }
  },
  computed: {
    effectiveVideoUrl() {
      return this.localVideoUrl || this.videoUrl || null
    },
    durationMs() {
      const s = Number(this.session?.timestamp_start)
      const e = Number(this.session?.timestamp_end)
      if (!isFinite(s) || !isFinite(e) || e <= s) return 0
      return e - s
    },
    totalPages() {
      const len = (this.events || []).length
      return Math.max(1, Math.ceil(len / this.itemsPerPage))
    },
    sessionTitle() {
      const barcode = this.session?.barcode ? `(${this.session.barcode})` : ''
      const device = this.session?.device_name || this.session?.device || ''
      return `${device} ${barcode}`.trim()
    }
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.currentPage = 1
        this.resetVideoOverlayState()
        this.localVideoUrl = this.videoUrl || null
        this.clipContextTimestampMs = this.getInitialClipContextTimestamp()
      } else {
        this.resetVideoOverlayState()
      }
    },
    session: {
      handler() {
        this.currentPage = 1
        this.resetVideoOverlayState()
        this.localVideoUrl = this.videoUrl || null
        this.clipContextTimestampMs = this.getInitialClipContextTimestamp()
      },
      deep: false
    },
    videoUrl: {
      handler(newVal) {
        // cuando el padre cambia el video inicial
        this.localVideoUrl = newVal || null
        this.clipContextTimestampMs = this.getInitialClipContextTimestamp()
      },
      immediate: true
    }
  },
  methods: {
    getSeekBaseMs() {
      // Base del segundo 0 del video: inicio real del mp4 (closestVideo.date)
      const clipStart = this.getClipStartMs()
      if (Number.isFinite(clipStart)) return clipStart
      // Fallback: si no hay clipStart, usar el mínimo unix_itme_stamp
      if (!Array.isArray(this.events) || this.events.length === 0) return NaN
      let min = Infinity
      for (const ev of this.events) {
        if (!ev || ev.unix_itme_stamp == null) continue
        const t = Number(ev.unix_itme_stamp)
        if (!Number.isFinite(t)) continue
        if (t < min) min = t
      }
      return min === Infinity ? NaN : min
    },
    // Inicio real del mp4 según backend (closestVideo.date)
    getClipStartMs() {
      const clipStart = Number(this.clipContextTimestampMs)
      return Number.isFinite(clipStart) && clipStart > 0 ? clipStart : NaN
    },
    formatTimestampMs(ts) {
      const n = Number(ts)
      if (!Number.isFinite(n) || n <= 0) return '-'
      try {
        return this.$Format && this.$Format.momentz
          ? this.$Format.momentz(new Date(n)).format('DD/MM/YYYY, HH:mm:ss.SSS')
          : new Date(n).toLocaleString()
      } catch (e) {
        return new Date(n).toLocaleString()
      }
    },
    formatRawTimestamp(ts) {
      const n = Number(ts)
      return Number.isFinite(n) ? String(n) : '-'
    },
    formatMmSs(totalSeconds) {
      const n = Number(totalSeconds)
      if (!Number.isFinite(n) || n < 0) return '-'
      const m = Math.floor(n / 60)
      const s = Math.floor(n % 60)
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    closeModal() {
      this.$emit('close')
    },
    onVideoError() {
      this.videoError = true
      this.clearBbox()
    },
    onVideoLoaded() {
      this.initializeBboxCanvas()
      this.$nextTick(() => {
        if (this.pendingHoverEvent) {
          const ev = this.pendingHoverEvent
          this.pendingHoverEvent = null
          this.seekAndDrawForHoverEvent(ev)
        }
      })
    },
    onVideoTimeUpdate() {
      const video = this.$refs.eventVideo
      if (video && Number.isFinite(video.currentTime)) {
        this.currentVideoSeconds = video.currentTime
        const clipStart = this.getClipStartMs()
        this.currentVideoAbsTimestampMs = Number.isFinite(clipStart)
          ? Math.round(clipStart + (video.currentTime * 1000))
          : null
      }

      if (this.currentHoverEvent && this.currentHoverEventVideoTime !== null) {
        if (!video) return
        const currentVideoTime = video.currentTime || 0
        const expected = this.currentHoverEventVideoTime
        const tolerance = 0.5
        if (Math.abs(currentVideoTime - expected) <= tolerance) {
          this.drawBbox()
        } else {
          this.clearBbox()
        }
      }
    },
    getInitialClipContextTimestamp() {
      const propTs = Number(this.videoContextTimestamp)
      if (isFinite(propTs) && propTs > 0) return propTs
      const s = Number(this.session?.timestamp_start)
      if (isFinite(s) && s > 0) return s
      return null
    },
    resetVideoOverlayState() {
      this.videoError = false
      this.currentHoverEvent = null
      this.currentHoverEventVideoTime = null
      this.pendingHoverEvent = null
      this.lastVideoSearchTimestamp = null
      this.hoverVideoLoading = false
      this.clearBbox()
      this.bboxCanvasContext = null
    },
    initializeBboxCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.bboxCanvas
        const video = this.$refs.eventVideo
        const stage = this.$refs.videoStage
        if (!canvas || !video) return

        const updateCanvasSize = () => {
          if (!canvas || !video) return
          const w = video.clientWidth || video.offsetWidth
          const h = video.clientHeight || video.offsetHeight
          canvas.width = w
          canvas.height = h
          canvas.style.width = w + 'px'
          canvas.style.height = h + 'px'
          if (this.bboxCanvasContext) {
            this.bboxCanvasContext.clearRect(0, 0, canvas.width, canvas.height)
          }
        }

        this.bboxCanvasContext = canvas.getContext('2d')
        updateCanvasSize()

        video.addEventListener('loadedmetadata', updateCanvasSize)
        // Asegurar re-cálculo cuando cambia el layout del contenedor
        if (stage && typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(() => updateCanvasSize())
          ro.observe(stage)
          ro.observe(video)
        }
        window.addEventListener('resize', updateCanvasSize)
      })
    },
    getEventTimestampMs(event) {
      if (!event) return NaN
      // Requerido: usar únicamente unix_itme_stamp (sin normalización)
      return Number(event.unix_itme_stamp)
    },
    formatEventVideoSeconds(event) {
      const startMs = this.getSeekBaseMs()
      const eventMs = this.getEventTimestampMs(event)
      if (!Number.isFinite(startMs) || !Number.isFinite(eventMs)) return '-'
      const seconds = (eventMs - startMs) / 1000
      if (!Number.isFinite(seconds)) return '-'
      return seconds.toFixed(3)
    },
    formatUnixItmeStamp(event) {
      const n = Number(event && event.unix_itme_stamp)
      return Number.isFinite(n) ? String(n) : '-'
    },
    onRowHover(event) {
      this.currentHoverEvent = event

      const video = this.$refs.eventVideo
      if (!video || !event) return

      const eventMs = this.getEventTimestampMs(event)
      const seekBaseMs = this.getSeekBaseMs()
      const durationMs = Number.isFinite(video.duration) ? video.duration * 1000 : NaN

      // Validar si el evento cae dentro del clip actual usando el inicio real del mp4
      const clipFileStartMs = this.getClipStartMs()
      const rangeStartMs = Number.isFinite(clipFileStartMs) ? clipFileStartMs : seekBaseMs

      const inCurrentClip =
        Number.isFinite(eventMs) &&
        Number.isFinite(rangeStartMs) &&
        Number.isFinite(durationMs) &&
        eventMs >= rangeStartMs &&
        eventMs <= (rangeStartMs + durationMs)

      // Si no está en el clip actual, buscar un clip para ese timestamp
      if (!inCurrentClip && Number.isFinite(eventMs)) {
        const deviceId = (this.session && this.session.deviceId) ? String(this.session.deviceId) : (event.deviceId ? String(event.deviceId) : null)
        if (deviceId) {
          if (this.hoverVideoLoading && this.lastVideoSearchTimestamp === eventMs) return
          this.pendingHoverEvent = event
          this.lastVideoSearchTimestamp = eventMs
          this.hoverVideoLoading = true
          this.loadClosestVideoForHover(eventMs, deviceId)
        }
        return
      }

      // Calcular segundo dentro del clip actual
      if (Number.isFinite(eventMs) && Number.isFinite(seekBaseMs)) {
        const seconds = Math.max(0, (eventMs - seekBaseMs) / 1000)
        this.currentHoverEventVideoTime = seconds
        if (Number.isFinite(video.duration)) {
          video.currentTime = Math.min(seconds, video.duration)
        }
        this.$nextTick(() => setTimeout(() => this.drawBbox(), 200))
      }
    },
    async loadClosestVideoForHover(timestampMs, deviceId) {
      try {
        const apiUrl = this.$URL + '/api/v1/records/find_closest_video'
        const payload = { timestamp: timestampMs, deviceId }
        const response = await this.$axios.post(apiUrl, payload)
        const data = response && response.data ? response.data : null
        const videoOrClosest = (data && data.video) || (data && data.closestVideo) || null
        let urlpath = videoOrClosest && videoOrClosest.urlpath ? videoOrClosest.urlpath : null
        if (!urlpath && data && data.urlpath) urlpath = data.urlpath
        if (!urlpath && videoOrClosest && videoOrClosest.url) urlpath = videoOrClosest.url
        if (!urlpath || typeof urlpath !== 'string') return
        if (!urlpath.startsWith('http') && !urlpath.startsWith('/')) urlpath = '/' + urlpath
        const fullUrl = urlpath.startsWith('http') ? urlpath : (this.$URL + urlpath)

        this.localVideoUrl = fullUrl
        // El backend retorna el "timestamp" del clip en `closestVideo.date`
        const startCandidate =
          (videoOrClosest && (videoOrClosest.date ?? videoOrClosest.timestamp_start ?? videoOrClosest.startTimestamp ?? videoOrClosest.start ?? videoOrClosest.start_time ?? videoOrClosest.time_start ?? videoOrClosest.ts_start)) ??
          (data && (data.timestamp_start ?? data.startTimestamp ?? data.start)) ??
          null
        const clipStart = Number(startCandidate)
        this.clipContextTimestampMs = isFinite(clipStart) ? clipStart : Number(timestampMs)
      } catch (e) {
        // ignore
      } finally {
        this.hoverVideoLoading = false
      }
    },
    seekAndDrawForHoverEvent(event) {
      const video = this.$refs.eventVideo
      if (!video || !event) return

      const eventMs = this.getEventTimestampMs(event)
      const seekBaseMs = this.getSeekBaseMs()
      let seconds = null
      if (Number.isFinite(eventMs) && Number.isFinite(seekBaseMs)) {
        seconds = Math.max(0, (eventMs - seekBaseMs) / 1000)
      }

      this.currentHoverEvent = event
      this.currentHoverEventVideoTime = seconds

      if (seconds !== null && !Number.isNaN(seconds) && Number.isFinite(video.duration)) {
        const target = Math.min(seconds, video.duration)
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked)
          this.$nextTick(() => this.drawBbox())
        }
        video.addEventListener('seeked', onSeeked)
        video.currentTime = target
        setTimeout(() => this.drawBbox(), 250)
      } else {
        this.$nextTick(() => this.drawBbox())
      }
    },
    onRowLeave() {
      this.currentHoverEvent = null
      this.currentHoverEventVideoTime = null
      this.clearBbox()
    },
    drawBbox() {
      if (!this.bboxCanvasContext || !this.currentHoverEvent || !this.$refs.eventVideo) return

      const canvas = this.$refs.bboxCanvas
      const video = this.$refs.eventVideo
      const ctx = this.bboxCanvasContext
      const event = this.currentHoverEvent

      if (!canvas || !video) return

      const displayWidth = video.clientWidth || video.offsetWidth
      const displayHeight = video.clientHeight || video.offsetHeight
      if (!displayWidth || !displayHeight) return

      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth
        canvas.height = displayHeight
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Calcular el rectángulo real donde se dibuja el frame dentro del elemento <video>
      // (cuando hay letterbox/pillarbox por relación de aspecto).
      const vw = video.videoWidth || displayWidth
      const vh = video.videoHeight || displayHeight
      const videoAR = vw / vh
      const boxAR = displayWidth / displayHeight
      let contentW = displayWidth
      let contentH = displayHeight
      let offsetX = 0
      let offsetY = 0
      if (Number.isFinite(videoAR) && videoAR > 0 && Number.isFinite(boxAR) && boxAR > 0) {
        if (boxAR > videoAR) {
          // Sobran pixeles en ancho (barras laterales)
          contentH = displayHeight
          contentW = displayHeight * videoAR
          offsetX = (displayWidth - contentW) / 2
        } else {
          // Sobran pixeles en alto (barras arriba/abajo)
          contentW = displayWidth
          contentH = displayWidth / videoAR
          offsetY = (displayHeight - contentH) / 2
        }
      }

      let bbox = null
      const raw = event.bbox || (event.content && event.content.bbox)
      if (raw) {
        try {
          bbox = typeof raw === 'string' ? JSON.parse(raw) : raw
        } catch (e) {
          return
        }
      }
      if (!bbox) return

      // Normalizar bbox a (x,y,w,h) numéricos
      let x = bbox.x ?? bbox.left ?? bbox.l ?? (Array.isArray(bbox) ? bbox[0] : undefined)
      let y = bbox.y ?? bbox.top ?? bbox.t ?? (Array.isArray(bbox) ? bbox[1] : undefined)
      let w = bbox.w ?? bbox.width ?? (Array.isArray(bbox) ? bbox[2] : undefined)
      let h = bbox.h ?? bbox.height ?? (Array.isArray(bbox) ? bbox[3] : undefined)

      // Soportar formato (x1,y1,x2,y2) en objetos o arrays
      const x2 = bbox.x2 ?? bbox.right ?? (Array.isArray(bbox) ? bbox[2] : undefined)
      const y2 = bbox.y2 ?? bbox.bottom ?? (Array.isArray(bbox) ? bbox[3] : undefined)

      x = parseFloat(x)
      y = parseFloat(y)
      w = parseFloat(w)
      h = parseFloat(h)

      if (![x, y, w, h].every(Number.isFinite)) return

      // Heurística para decidir si (w,h) son realmente (x2,y2)
      // Normalizado: si x+w y y+h exceden el rango, probablemente son x2/y2.
      const allSmall = x >= 0 && x <= 1.2 && y >= 0 && y <= 1.2 && w >= 0 && w <= 1.2 && h >= 0 && h <= 1.2
      const looksLikeWidthHeight = allSmall && (x + w) <= 1.05 && (y + h) <= 1.05
      const looksLikeX2Y2 = allSmall && !looksLikeWidthHeight && w > x && h > y
      if (looksLikeX2Y2) {
        w = w - x
        h = h - y
      }

      // Si viene explícito x2/y2 en objeto, preferirlo
      const nx2 = parseFloat(x2)
      const ny2 = parseFloat(y2)
      if (Number.isFinite(nx2) && Number.isFinite(ny2) && nx2 > x && ny2 > y) {
        // Si parece normalizado o está dentro de un rango razonable
        if ((nx2 <= 1.2 && ny2 <= 1.2) || (nx2 <= (vw * 1.2) && ny2 <= (vh * 1.2))) {
          w = nx2 - x
          h = ny2 - y
        }
      }

      // Si viene normalizado (0..1), convertir a pixeles de display
      // Si viene en pixeles, escalar según video.videoWidth/video.videoHeight
      let fx, fy, fw, fh
      const isNormalized = x >= 0 && x <= 1 && y >= 0 && y <= 1 && w > 0 && w <= 1.5 && h > 0 && h <= 1.5
      if (isNormalized) {
        fx = offsetX + (x * contentW)
        fy = offsetY + (y * contentH)
        fw = w * contentW
        fh = h * contentH
      } else {
        const scaleX = contentW / vw
        const scaleY = contentH / vh
        fx = offsetX + (x * scaleX)
        fy = offsetY + (y * scaleY)
        fw = w * scaleX
        fh = h * scaleY
      }

      ctx.strokeStyle = '#ff0000'
      ctx.lineWidth = 3
      ctx.strokeRect(fx, fy, fw, fh)

      if (event.tag) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.8)'
        ctx.fillRect(fx, Math.max(0, fy - 20), Math.max(40, fw), 20)
        ctx.fillStyle = '#ffffff'
        ctx.font = '12px Arial'
        ctx.fillText(String(event.tag), fx + 5, Math.max(12, fy - 6))
      }
    },
    clearBbox() {
      if (this.bboxCanvasContext && this.$refs.bboxCanvas) {
        const canvas = this.$refs.bboxCanvas
        this.bboxCanvasContext.clearRect(0, 0, canvas.width, canvas.height)
      }
    },
    formatDateTimeFromTs(ts) {
      const n = Number(ts)
      if (!isFinite(n) || n <= 0) return '-'
      try {
        return this.$Format && this.$Format.momentz ? this.$Format.momentz(new Date(n)).format('DD/MM/YYYY, HH:mm:ss') : new Date(n).toLocaleString()
      } catch (e) {
        return new Date(n).toLocaleString()
      }
    },
    formatDurationMs(ms) {
      const n = Number(ms)
      if (!isFinite(n) || n <= 0) return '0s'
      const totalSeconds = Math.floor(n / 1000)
      const h = Math.floor(totalSeconds / 3600)
      const m = Math.floor((totalSeconds % 3600) / 60)
      const s = totalSeconds % 60
      if (h > 0) return `${h}h ${m}m ${s}s`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
    getPaginatedEvents() {
      const list = this.events || []
      const start = (this.currentPage - 1) * this.itemsPerPage
      return list.slice(start, start + this.itemsPerPage)
    },
    getEventNumber(indexInPage) {
      return (this.currentPage - 1) * this.itemsPerPage + indexInPage + 1
    },
    previousPage() {
      if (this.currentPage > 1) this.currentPage -= 1
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1
    },
    getPaginationStart() {
      const len = (this.events || []).length
      if (len === 0) return 0
      return (this.currentPage - 1) * this.itemsPerPage + 1
    },
    getPaginationEnd() {
      const len = (this.events || []).length
      return Math.min(this.currentPage * this.itemsPerPage, len)
    },
    getShowingEventsRangeText() {
      const s = this.$ml.get('showing_events_range') || 'Showing {0} - {1} of {2} events'
      return s.replace('{0}', this.getPaginationStart()).replace('{1}', this.getPaginationEnd()).replace('{2}', (this.events || []).length)
    },
    getPageOfText() {
      const s = this.$ml.get('page_of') || 'Page {0} of {1}'
      return s.replace('{0}', this.currentPage).replace('{1}', this.totalPages)
    },
    formatEventDate(event) {
      if (!event) return '-'
      // prefer already formatted string
      if (event.date) return event.date
      const ts = event.unix_itme_stamp || event.timestamp || event.createdAt || event.time || event.date_utc
      if (!ts) return '-'
      try {
        return this.$Format && this.$Format.format_date ? this.$Format.format_date(ts) : new Date(ts).toLocaleString()
      } catch (e) {
        return '-'
      }
    },
    getEvidenceUrl(event) {
      if (!event) return null
      const url = event.url_evidence || event.evidence || event.img || event.image
      if (!url) return null
      if (typeof url !== 'string') return null
      if (url === '/img/no_img_found.png') return null
      return url
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-container {
  width: 92%;
  max-width: 980px;
  max-height: 92vh;
  background: #1f1f1f;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0,0,0,.55);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  color: #fff;
}

.modal-header-counter {
  background: #6a1b9a;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.event-subtitle {
  margin: 4px 0 0;
  opacity: 0.9;
  font-size: 13px;
}

.close-button {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 18px;
}

.event-description {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 10px 16px;
  background: #2b2b2b;
  color: #fff;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.description-item label {
  display: block;
  font-size: 11px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.description-item span {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
}

.timestamp-raw {
  margin-top: 2px;
  font-size: 11px;
  opacity: 0.75;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.modal-content {
  padding: 14px 16px 18px;
  overflow: auto;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: start;
}

.video-section h3,
.events-table-section h3 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #fff;
}

.video-container {
  background: #111;
  border-radius: 10px;
  padding: 10px;
  position: relative;
}

.video-stage {
  position: relative;
  width: 100%;
}

.event-video {
  width: 100%;
  max-height: 320px;
  border-radius: 8px;
  background: #000;
  position: relative;
  z-index: 1;
  display: block;
}

.bbox-canvas {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  border-radius: 8px;
  z-index: 2;
}

.video-meta {
  margin-top: 8px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}

.video-meta-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.3;
}

.video-meta-label {
  opacity: 0.8;
  font-weight: 600;
}

.video-meta-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.video-meta-sep {
  opacity: 0.5;
}

.no-video {
  text-align: center;
  padding: 40px 0;
  color: rgba(255,255,255,0.75);
}

.table-container {
  background: #111;
  border-radius: 10px;
  overflow: hidden;
}

.table-wrapper {
  width: 100%;
}

.events-table {
  width: 100%;
  border-collapse: collapse;
  color: #fff;
  font-size: 13px;
  table-layout: fixed;
}

.events-table thead {
  background: #2b2b2b;
}

.events-table th,
.events-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  text-align: center !important;
  vertical-align: middle;
}

.event-row td {
  transition: background-color 120ms ease, color 120ms ease;
}

.event-row:hover td {
  background: rgba(255, 255, 255, 0.06);
}

/* Forzar centrado específico de la columna Tag */
.events-table th:nth-child(3),
.events-table td:nth-child(3) {
  text-align: center !important;
}

/* Anchos por columna para alinear header/body */
.events-table th:nth-child(1),
.events-table td:nth-child(1) {
  width: 52px;
}
.events-table th:nth-child(2),
.events-table td:nth-child(2) {
  width: 90px;
}
.events-table th:nth-child(3),
.events-table td:nth-child(3) {
  width: 170px;
}
.events-table th:nth-child(5),
.events-table td:nth-child(5) {
  width: 140px;
}

.table-body-scroll {
  max-height: 260px;
  overflow-y: auto;
}

.loading-events,
.no-events {
  text-align: center;
  padding: 18px 0;
  color: rgba(255,255,255,0.75);
}

.evidence-link {
  color: #4fc3f7;
  text-decoration: none;
  font-weight: 600;
}

.table-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #1c1c1c;
  color: rgba(255,255,255,0.85);
  font-size: 12px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-pagination {
  border: none;
  background: rgba(255,255,255,0.08);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
}

.btn-pagination:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .event-description {
    grid-template-columns: repeat(2, 1fr);
  }

  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .event-description {
    grid-template-columns: 1fr;
  }
}
</style>

